import {inject, injectable} from 'inversify';
import {makeAutoObservable} from 'mobx';

import type {Destructible} from '../../../../utils/store';
import type {Nullable} from '../../../../utils/utility-types';

import {DateTimeModelFactory} from '../../../../core/domain/Date/DateTime.factory';
import {BaseNavigationService} from '../../../../core/system/navigation/base/BaseNavigation.service';
import {DataFilterState} from '../../models/DataFilterState';
import {DATA_FILTER_STORE_COMMON_ID} from '../DataFilter/utils';

@injectable()
export class DataFilterStorageManagerStore implements Destructible {
  public id = DATA_FILTER_STORE_COMMON_ID;

  constructor(
    @inject(BaseNavigationService)
    private readonly navigationService: BaseNavigationService,
    @inject(DateTimeModelFactory)
    private readonly dateTimeModelFactory: DateTimeModelFactory,
  ) {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  init(id: string) {
    this.id = id;

    return this;
  }

  destruct() {}

  read(): DataFilterState {
    const navigationParams = this.navigationService.getQueryParams();

    const dates = this.stringToDates(navigationParams.get(this.datesKey));
    const search = navigationParams.get(this.searchKey) || '';
    const fields = this.stringToFields(navigationParams.get(this.fieldsKey));

    return new DataFilterState(search, dates, fields);
  }

  write(state: DataFilterState) {
    const params = {
      [this.datesKey]: this.datesToString(state.datesFilter),
      [this.fieldsKey]: this.fieldsToString(state.fields),
      [this.searchKey]: this.searchToString(state.search),
    };

    this.navigationService.writeQueryParams(params);

    return this;
  }

  private get datesKey() {
    return `${this.id}_dates`;
  }

  private get fieldsKey() {
    return `${this.id}_fields`;
  }

  private get searchKey() {
    return `${this.id}_search`;
  }

  private dateOrNull(timestamp?: string) {
    return timestamp
      ? this.dateTimeModelFactory.create(Number(timestamp))
      : null;
  }

  private datesToString(dates: DataFilterState.DatesFilter): Nullable<string> {
    if (!dates) {
      return null;
    }

    const {end, start} = dates;

    return [start, end]
      .map(date => (date ? date.value.toString() : ''))
      .join(',');
  }

  private fieldsToString(fields: DataFilterState['fields']): Nullable<string> {
    if (!fields) {
      return null;
    }

    return Array.from(fields.values())
      .map(({key, values}) => `${key}:${Array.from(values).join(',')}`)
      .join(';');
  }

  private searchToString(search: DataFilterState['search']): Nullable<string> {
    return search || null;
  }

  private stringToDates(datesStr?: string): DataFilterState['datesFilter'] {
    if (!datesStr) {
      return null;
    }

    const timestamps = datesStr.split(',');
    const start = this.dateOrNull(timestamps[0]);
    const end = this.dateOrNull(timestamps[1]);

    return {end, start};
  }

  private stringToFields(fieldsStr?: string): DataFilterState['fields'] {
    if (!fieldsStr) {
      return null;
    }

    const fieldsStrings = fieldsStr.split(';');
    const entries: [string, DataFilterState.Field][] = fieldsStrings.map(
      field => {
        const [key, values] = field.split(':');

        return [key!, {key: key!, values: new Set(values!.split(','))}];
      },
    );

    return new Map(entries);
  }
}
